import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Sun, Moon, FlaskConical, Menu } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { useState } from 'react';
import { NAV } from '@/constants/testIds/healthspan';
import FooterDisclaimer from '@/components/FooterDisclaimer';
import FirstVisitDisclaimerModal from '@/components/FirstVisitDisclaimerModal';

const navItems = [
  { to: '/', label: 'Home', tid: NAV.home, end: true },
  { to: '/match', label: 'Match', tid: NAV.match },
  { to: '/protocols', label: 'Protocols', tid: NAV.protocols },
  { to: '/trials', label: 'Trials', tid: NAV.trials },
  { to: '/pricing', label: 'Pricing', tid: NAV.pricing },
];

function TopNavLinks({ onClick }) {
  return (
    <>
      {navItems.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          data-testid={it.tid}
          onClick={onClick}
          className={({ isActive }) =>
            `text-sm font-medium px-3 py-2 rounded-[var(--radius-sm)] transition-colors duration-150 ${
              isActive
                ? 'bg-[color:var(--surface-2)] text-[color:var(--fg)]'
                : 'text-[color:var(--muted-fg)] hover:text-[color:var(--fg)] hover:bg-[color:var(--surface-2)]'
            }`
          }
        >
          {it.label}
        </NavLink>
      ))}
    </>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  return (
    <div className="app-shell">
      <FirstVisitDisclaimerModal />
      <header className="sticky top-0 z-30 border-b border-[color:var(--border-color)] bg-[color:var(--surface)]/80 backdrop-blur supports-[backdrop-filter]:bg-[color:var(--surface)]/70">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid={NAV.brand} className="flex items-center gap-2 group">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-[var(--radius-sm)] bg-[color:var(--brand-primary)] text-[color:var(--brand-primary-fg)] shadow-[var(--shadow-sm)]">
              <FlaskConical className="h-5 w-5" aria-hidden="true" />
            </span>
            <div className="leading-tight">
              <div className="text-sm font-semibold tracking-tight">Telogenis</div>
              <div className="text-[11px] text-[color:var(--muted-fg)] -mt-0.5">Healthspan Engine</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            <TopNavLinks />
          </nav>
          <div className="flex items-center gap-2">
            <button
              data-testid={NAV.themeToggle}
              onClick={toggle}
              aria-label="Toggle theme"
              className="inline-flex h-9 w-9 items-center justify-center rounded-[var(--radius-sm)] border border-[color:var(--border-color)] hover:bg-[color:var(--surface-2)] transition-colors duration-150"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            {user ? (
              <div className="hidden md:flex items-center gap-2">
                <NavLink to="/dashboard" data-testid={NAV.dashboard} className="text-sm font-medium px-3 py-2 rounded-[var(--radius-sm)] text-[color:var(--muted-fg)] hover:text-[color:var(--fg)] hover:bg-[color:var(--surface-2)] transition-colors duration-150">Dashboard</NavLink>
                {user.role === 'admin' && (
                  <NavLink to="/admin" data-testid={NAV.admin} className="text-sm font-medium px-3 py-2 rounded-[var(--radius-sm)] text-[color:var(--accent-gold)] hover:bg-[color:var(--surface-2)] transition-colors duration-150">Admin</NavLink>
                )}
                <Button data-testid={NAV.logout} variant="outline" size="sm" onClick={() => { logout(); nav('/'); }}>Sign out</Button>
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-2">
                <NavLink to="/login" data-testid={NAV.login} className="text-sm font-medium px-3 py-2 rounded-[var(--radius-sm)] text-[color:var(--muted-fg)] hover:text-[color:var(--fg)] hover:bg-[color:var(--surface-2)] transition-colors duration-150">Sign in</NavLink>
                <Button data-testid={NAV.register} size="sm" onClick={() => nav('/register')} className="bg-[color:var(--brand-primary)] text-[color:var(--brand-primary-fg)] hover:bg-[color:var(--brand-primary-2)]">Create account</Button>
              </div>
            )}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button data-testid={NAV.mobileMenu} className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-[var(--radius-sm)] border border-[color:var(--border-color)]" aria-label="Open menu">
                  <Menu className="h-4 w-4" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <SheetTitle className="sr-only">Navigation</SheetTitle>
                <div className="flex flex-col gap-1 mt-6">
                  <TopNavLinks onClick={() => setOpen(false)} />
                  {user ? (
                    <>
                      <NavLink to="/dashboard" onClick={() => setOpen(false)} className="text-sm font-medium px-3 py-2 rounded-[var(--radius-sm)] text-[color:var(--muted-fg)] hover:bg-[color:var(--surface-2)]">Dashboard</NavLink>
                      {user.role === 'admin' && <NavLink to="/admin" onClick={() => setOpen(false)} className="text-sm font-medium px-3 py-2 rounded-[var(--radius-sm)] text-[color:var(--accent-gold)] hover:bg-[color:var(--surface-2)]">Admin</NavLink>}
                      <Button variant="outline" className="mt-3" onClick={() => { setOpen(false); logout(); nav('/'); }}>Sign out</Button>
                    </>
                  ) : (
                    <>
                      <NavLink to="/login" onClick={() => setOpen(false)} className="text-sm font-medium px-3 py-2 rounded-[var(--radius-sm)] text-[color:var(--muted-fg)] hover:bg-[color:var(--surface-2)]">Sign in</NavLink>
                      <NavLink to="/register" onClick={() => setOpen(false)} className="text-sm font-medium px-3 py-2 rounded-[var(--radius-sm)] text-[color:var(--muted-fg)] hover:bg-[color:var(--surface-2)]">Create account</NavLink>
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        {children}
      </main>

      <FooterDisclaimer key={loc.pathname} />
    </div>
  );
}
